import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  IonAlert, IonBackButton, IonBadge, IonButton, IonButtons, IonCard, IonCardContent, IonCardHeader, IonCardSubtitle,
  IonCardTitle, IonCheckbox, IonContent, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonModal,
  IonNote, IonPage, IonSelect, IonSelectOption, IonSpinner,
  IonText, IonTitle, IonToast, IonToolbar,
} from '@ionic/react';
import { informationOutline } from 'ionicons/icons';
import {
  allocateCateringLots, canonicalDdtSupplierIdentities, CateringDischargeDraftSchema,
  CateringDischargeSnapshotSchema, computeCateringEventRequirements, computeCateringProcurement,
  computeRecipeCost, convertQty, dedupeStockInByDdt, detectCostAnomalies, diagnoseRecipeCost,
  resolveSeasonCostBasis, StockCommandV1Schema, suggestCateringDocuments,
  type CateringDischargeLine, type CateringEvent, type CateringLotCandidate,
  type CateringQuoteRevision, type FoodCostSignal, type Recipe, type SeasonCostObservation,
  type StockSourceLineV1,
} from '@trackeat/shared';
import { toUserMessage } from '../../lib/errors.js';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import {
  useDefaultVatRate, useEnqueueMaster, useEnqueueStockCommand, useEventQuotesAccessGate, useEvents, useMasters, useStockProjection,
} from '../../lib/data/queries.js';
import { getSyncEngine } from '../../lib/sync/engine.js';
import { projectFoodCostPairs, type CostAdjustmentEventRow } from '../../lib/foodCostProjection.js';
import {
  selectActivePhysicalPurchaseReceipts, type PhysicalPurchaseEvent, type PhysicalPurchasePayload,
} from '../../lib/physicalPurchaseReceipts.js';
import {
  EventQuoteSimulator, type EventQuoteRecipeOption, type EventQuoteResolvedCost, type EventQuoteSaveDraft,
} from '../haccp/EventQuoteSimulator.js';
import {
  CATERING_GUIDE_LIMITS, CATERING_GUIDE_PREREQUISITES, CATERING_GUIDE_RESULTS,
  CATERING_GUIDE_STEPS,
} from './cateringGuide.js';
import './EventQuotesPage.css';
import { CATERING_MISSING_LABEL, cateringAgenda, cateringDateLabel, cateringEditorId, cateringNextAction, type CateringAgendaFilter } from './cateringOperations.js';

type FoodRow = {
  id: string; name?: string; category?: string | null; default_uom?: string | null;
  current_supplier_id?: string | null; prev_unit_cost?: number | null;
  cost_uom_reviewed_at?: string | null; cost_uom_reviewed_uom?: string | null;
};
type StockEvt = Omit<PhysicalPurchaseEvent, 'payload'> & { payload: PhysicalPurchasePayload & {
  food_id?: string; uom?: string | null; unit_cost?: number | string | null; currency?: string | null;
  document_date?: string | null; document_id?: string | null; document_number?: string | null;
  supplier_id?: string | null; supplier_identity?: string | null;
  stock_qty?: number | string | null; qty?: number | string | null;
} };
type DestinationRow = { id: string; name: string; business_id?: string | null; archived_at?: string | null };
type SupplierRow = { id: string; name: string; piva?: string | null; aliases?: string[] | null };
type DocumentRow = { id: string; business_id?: string | null; supplier_id?: string | null; document_number?: string | null; document_date?: string | null; kind?: string | null; archived_at?: string | null };
type ShipmentRow = { id: string; status?: string | null; kind?: string | null; doc_number?: number | null; doc_year?: number | null; archived_at?: string | null };

const eur = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
const qty = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 3 });

const STATUS_LABEL: Record<CateringEvent['status'], string> = {
  draft: 'Bozza', quoted: 'Preventivato', confirmed: 'Confermato', in_production: 'In produzione', completed: 'Concluso', cancelled: 'Annullato',
};

function mondayOf(date = new Date()): string {
  const result = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = result.getUTCDay() || 7;
  result.setUTCDate(result.getUTCDate() - day + 1);
  return result.toISOString().slice(0, 10);
}

function revisionToDraft(revision: CateringQuoteRevision | undefined): EventQuoteSaveDraft | null {
  if (!revision) return null;
  const input = revision.input_snapshot as Omit<EventQuoteSaveDraft, 'eventName' | 'customerName' | 'eventDate' | 'seasonStart' | 'seasonEnd' | 'asOf' | 'guestsMin' | 'guestsExpected' | 'guestsMax' | 'selectedRecipeIds' | 'logisticsMode' | 'destinationId' | 'scenarios'>;
  return {
    eventName: revision.event_name, customerName: revision.customer_name, eventDate: revision.event_date,
    seasonStart: revision.season_start, seasonEnd: revision.season_end, asOf: revision.as_of,
    guestsMin: revision.guests_min, guestsExpected: revision.guests_expected, guestsMax: revision.guests_max,
    selectedRecipeIds: revision.selected_recipe_ids, logisticsMode: revision.logistics_mode,
    destinationId: revision.destination_id, ...input,
    scenarios: revision.result_snapshot as unknown as EventQuoteSaveDraft['scenarios'],
  };
}

function eventBusinessId(event: StockEvt): string | null {
  const payloadBusinessId = event.payload.business_id;
  return event.business_id
    ?? (typeof payloadBusinessId === 'string' ? payloadBusinessId : null);
}

function round6(value: number): number {
  return Math.round(value * 1_000_000) / 1_000_000;
}

function stockSourceLine(input: {
  eventId: string;
  index: number;
  line: CateringDischargeLine;
  stockUom: string;
  unitCost: number | null;
  currency: string;
  operatorName: string | null;
}): StockSourceLineV1 {
  const factor = convertQty(1, input.line.uom, input.stockUom);
  if (factor == null || factor <= 0) throw new Error(`UdM incompatibili: ${input.line.uom} → ${input.stockUom}`);
  const sourceUnitPrice = input.unitCost == null ? null : input.unitCost * factor;
  return {
    food_id: input.line.food_id,
    batch_lot: input.line.batch_lot,
    expiration_date: input.line.expiration_date,
    supplier_id: input.line.supplier_id,
    document_id: input.line.document_id,
    storage_unit_id: input.line.storage_unit_id,
    line_ref: `catering:${input.eventId}:input:${input.index + 1}`,
    lot_action: 'recorded_only',
    operator_name: input.operatorName,
    source_qty: input.line.consumed_qty,
    source_uom: input.line.uom,
    source_unit_price: sourceUnitPrice,
    source_line_total: sourceUnitPrice == null ? null : sourceUnitPrice * input.line.consumed_qty,
    currency: input.currency,
    stock_uom: input.stockUom,
    conversion: { factor, source: input.line.uom.trim().toLowerCase() === input.stockUom.trim().toLowerCase() ? 'identity' : 'metric' },
  };
}

export function EventQuotesPage(): JSX.Element {
  const access = useEventQuotesAccessGate();
  const { pathname } = useLocation();
  const { profile } = useAuth();
  if (access.checking) return <GatePage><IonSpinner /><p>Verifica accesso al modulo…</p></GatePage>;
  if (!access.enabled) return <GatePage><IonText color="medium"><p>{access.error ? 'Non è stato possibile verificare l’accesso. ' : ''}Il modulo <b>Sale Trattenimenti / Catering</b> non è attivo per questo cliente.</p></IonText></GatePage>;
  return <EnabledCateringPage key={`${pathname}:${profile?.active_business_id}`} editorId={cateringEditorId(pathname)} />;
}

function GatePage({ children }: { children: React.ReactNode }): JSX.Element {
  return <IonPage><IonHeader><IonToolbar><IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons><IonTitle>Sale Trattenimenti / Catering</IonTitle></IonToolbar></IonHeader><IonContent className="ion-padding"><div style={{ textAlign: 'center', marginTop: 40 }}>{children}</div></IonContent></IonPage>;
}

function EnabledCateringPage({ editorId }: { editorId: string | null }): JSX.Element {
  const { profile } = useAuth();
  const history = useHistory();
  const enqueue = useEnqueueMaster();
  const defaultVatRate = useDefaultVatRate();
  const { data: eventsRaw = [], isLoading: eventsLoading } = useMasters<CateringEvent>('catering_events');
  const { data: recipes = [] } = useMasters<Recipe>('recipes');
  const { data: foods = [] } = useMasters<FoodRow>('foods');
  const { data: destinations = [] } = useMasters<DestinationRow>('destinations');
  const { data: suppliers = [] } = useMasters<SupplierRow>('suppliers');
  const { data: documents = [] } = useMasters<DocumentRow>('documents');
  const { data: shipments = [] } = useMasters<ShipmentRow>('shipments');
  const { data: stockIn = [] } = useEvents('stock_in', null);
  const { data: stockOut = [] } = useEvents('stock_out', null);
  const { data: costAdjustments = [] } = useEvents('cost_adjustment', null);
  const canonicalUomByFood = useMemo(() => Object.fromEntries(foods.filter((food) => food.default_uom).map((food) => [food.id, food.default_uom!])), [foods]);
  const { data: stockProjection } = useStockProjection({ canonicalUomByFood });
  const [view, setView] = useState<'events' | 'procurement' | 'production' | 'logistics'>('events');
  const [selectedEventId, setSelectedEventId] = useState('');
  const [guideOpen, setGuideOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CateringEvent | null>(null);
  const [weekStart, setWeekStart] = useState(mondayOf());
  const [toast, setToast] = useState<string | null>(null);
  const [agendaFilter, setAgendaFilter] = useState<CateringAgendaFilter>('active');
  const [search, setSearch] = useState('');
  const initialQuoteDraft = useMemo(() => revisionToDraft(editingEvent?.quote_revisions.at(-1)), [editingEvent]);
  const today = new Date().toLocaleDateString('sv-SE');

  const events = useMemo(() => eventsRaw
    .filter((event) => event.business_id === profile?.active_business_id)
    .sort((a, b) => a.event_date.localeCompare(b.event_date)), [eventsRaw, profile?.active_business_id]);
  const visibleEvents = useMemo(() => cateringAgenda(events, agendaFilter, search, today), [events, agendaFilter, search, today]);
  const upcomingEvents = useMemo(() => cateringAgenda(events, 'week', '', today), [events, today]);
  useEffect(() => {
    if (editorId && editorId !== 'new' && !editingEvent) {
      const found = events.find((event) => event.id === editorId);
      if (found) setEditingEvent(found);
    }
  }, [editorId, editingEvent, events]);
  const activeStockIn = useMemo(() => selectActivePhysicalPurchaseReceipts(stockIn as StockEvt[], stockOut as PhysicalPurchaseEvent[]), [stockIn, stockOut]);
  const supplierIdentities = useMemo(() => canonicalDdtSupplierIdentities(suppliers), [suppliers]);
  const documentById = useMemo(() => new Map(documents.map((document) => [document.id, document])), [documents]);
  const seasonObservations = useMemo<SeasonCostObservation[]>(() => {
    const observations: SeasonCostObservation[] = [];
    const withSupplierIdentity = activeStockIn.map((event) => ({ ...event, payload: {
      ...event.payload,
      supplier_identity: event.payload.supplier_id ? supplierIdentities.get(event.payload.supplier_id) ?? null : null,
    } }));
    for (const event of dedupeStockInByDdt(withSupplierIdentity)) {
      const payload = event.payload;
      const foodId = typeof payload.food_id === 'string' ? payload.food_id : '';
      const quantity = Number(payload.stock_qty ?? payload.qty);
      const unitCost = Number(payload.unit_cost);
      const uom = typeof payload.uom === 'string' ? payload.uom.trim() : '';
      const documentId = typeof payload.document_id === 'string' ? payload.document_id : null;
      const document = documentId ? documentById.get(documentId) : null;
      const documentDate = typeof payload.document_date === 'string' ? payload.document_date : document?.document_date ?? null;
      const recordedAt = event.server_ts ?? event.client_ts ?? null;
      const sourceRef = event.server_id ?? event.id ?? event.client_uuid ?? '';
      if (!foodId || !(quantity > 0) || !(unitCost > 0) || !uom || !documentDate || !recordedAt || !sourceRef) continue;
      observations.push({
        foodId, dedupeKey: sourceRef, revision: event.server_rev ?? event.rev ?? 0,
        documentDate, recordedAt, qty: quantity, unitCost, uom,
        currency: typeof payload.currency === 'string' && payload.currency.trim() ? payload.currency : 'EUR',
        source: document?.kind === 'invoice' || document?.kind === 'fattura' ? 'invoice' : 'ddt',
        validated: true, documentRef: payload.document_number ?? document?.document_number ?? documentId ?? sourceRef,
        sourceRef,
      });
    }
    return observations;
  }, [activeStockIn, documentById, supplierIdentities]);
  const costPairs = useMemo(() => projectFoodCostPairs(foods, costAdjustments as CostAdjustmentEventRow[]), [costAdjustments, foods]);
  const foodUnitCost = useMemo(() => (id: string): number | null => costPairs.get(id)?.cost ?? null, [costPairs]);
  const foodUom = useMemo(() => (id: string): string | null => costPairs.get(id)?.uom ?? null, [costPairs]);
  const highAnomalyFoodIds = useMemo(() => {
    const loadUoms = new Map<string, Array<{ uom: string; observedAt: string | null }>>();
    for (const event of activeStockIn) {
      const id = event.payload.food_id; const uom = event.payload.uom;
      if (!id || !uom) continue;
      const rows = loadUoms.get(id) ?? []; rows.push({ uom: String(uom), observedAt: event.server_ts ?? event.client_ts ?? null }); loadUoms.set(id, rows);
    }
    const signals: FoodCostSignal[] = foods.map((food) => ({
      id: food.id, name: food.name ?? food.id.slice(0, 8), category: food.category ?? null,
      defaultUom: food.default_uom ?? null, lastUnitCost: costPairs.get(food.id)?.cost ?? null,
      prevUnitCost: food.prev_unit_cost ?? null, loadUomObservations: loadUoms.get(food.id) ?? [],
      uomReviewedAt: food.cost_uom_reviewed_at ?? null, uomReviewedAs: food.cost_uom_reviewed_uom ?? null,
    }));
    return new Set(detectCostAnomalies(signals).filter((item) => item.severity === 'alta').map((item) => item.foodId));
  }, [activeStockIn, costPairs, foods]);
  const options = useMemo<EventQuoteRecipeOption[]>(() => {
    const recipesById = new Map(recipes.map((recipe) => [recipe.id, recipe]));
    const hasCriticalAnomaly = (recipe: Recipe, seen: Set<string>): boolean => {
      if (seen.has(recipe.id)) return false;
      const next = new Set(seen).add(recipe.id);
      if ((recipe.ingredients ?? []).some((ingredient) => highAnomalyFoodIds.has(ingredient.food_id))) return true;
      return (recipe.parent_recipe_ids ?? []).some((id) => { const child = recipesById.get(id); return child ? hasCriticalAnomaly(child, next) : false; });
    };
    return recipes.filter((recipe) => !recipe.archived_at && recipe.variant_of_recipe_id == null)
      .map((recipe) => ({ recipe, diagnosis: diagnoseRecipeCost(recipe, { recipesById, foodUnitCost, foodUom }) }))
      .filter(({ recipe, diagnosis }) => diagnosis.severity === 'green' && diagnosis.costPerPortion != null && !hasCriticalAnomaly(recipe, new Set()))
      .map(({ recipe, diagnosis }) => ({ id: recipe.id, name: recipe.name, costPerPortion: diagnosis.costPerPortion! }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [recipes, foodUnitCost, foodUom, highAnomalyFoodIds]);
  const optionById = useMemo(() => new Map(options.map((option) => [option.id, option])), [options]);
  const foodById = useMemo(() => new Map(foods.map((food) => [food.id, food])), [foods]);
  const supplierById = useMemo(() => new Map(suppliers.map((supplier) => [supplier.id, supplier.name])), [suppliers]);
  const lotCandidates = useMemo<CateringLotCandidate[]>(() => {
    const metadata = new Map<string, Pick<CateringLotCandidate, 'supplierId' | 'documentId' | 'storageUnitId'>>();
    for (const event of activeStockIn) {
      if (eventBusinessId(event as StockEvt) !== profile?.active_business_id) continue;
      const foodId = typeof event.payload.food_id === 'string' ? event.payload.food_id : null;
      if (!foodId) continue;
      const batchLot = typeof event.payload.batch_lot === 'string' ? event.payload.batch_lot : null;
      const key = `${foodId}|${batchLot ?? ''}`;
      const current = metadata.get(key) ?? { supplierId: null, documentId: null, storageUnitId: null };
      current.supplierId ??= typeof event.payload.supplier_id === 'string' ? event.payload.supplier_id : null;
      current.documentId ??= typeof event.payload.document_id === 'string' ? event.payload.document_id : null;
      current.storageUnitId ??= typeof event.payload.storage_unit_id === 'string' ? event.payload.storage_unit_id : null;
      metadata.set(key, current);
    }
    return (stockProjection?.balances ?? []).filter((balance) => balance.availableQty > 0.000_001 && !balance.closed).map((balance) => {
      const carried = metadata.get(`${balance.foodId}|${balance.batchLot ?? ''}`);
      return {
        foodId: balance.foodId, batchLot: balance.batchLot, expirationDate: balance.expirationDate,
        availableQty: balance.availableQty, uom: balance.uom,
        supplierId: carried?.supplierId ?? null, documentId: carried?.documentId ?? null,
        storageUnitId: carried?.storageUnitId ?? balance.storageUnitId,
      };
    });
  }, [activeStockIn, profile?.active_business_id, stockProjection?.balances]);
  const selectedEvent = events.find((event) => event.id === selectedEventId) ?? events.find((event) => ['confirmed', 'in_production'].includes(event.status)) ?? events[0] ?? null;
  const procurement = useMemo(() => computeCateringProcurement({
    weekStart, events, recipes,
    availableStock: (stockProjection?.balances ?? []).filter((balance) => balance.availableQty > 0).map((balance) => ({ foodId: balance.foodId, qty: balance.availableQty, uom: balance.uom })),
    foodUomById: canonicalUomByFood,
  }), [canonicalUomByFood, events, recipes, stockProjection?.balances, weekStart]);
  const resolveRecipeSeasonCost = useCallback((recipeId: string, window: { seasonStart: string; seasonEnd: string; asOf: string }): EventQuoteResolvedCost => {
    const recipe = recipes.find((item) => item.id === recipeId);
    if (!recipe) return { costPerPortion: null, source: 'unavailable', includedObservations: 0, latestDocumentDate: null };
    const recipesById = new Map(recipes.map((item) => [item.id, item]));
    const resolvedByFood = new Map<string, ReturnType<typeof resolveSeasonCostBasis>>();
    const resolveFood = (foodId: string) => {
      const cached = resolvedByFood.get(foodId);
      if (cached) return cached;
      const resolved = resolveSeasonCostBasis({ foodId, observations: seasonObservations, ...window, strategy: 'weighted_average' });
      resolvedByFood.set(foodId, resolved); return resolved;
    };
    const result = computeRecipeCost(recipe, {
      recipesById,
      foodUnitCost: (foodId) => { const resolved = resolveFood(foodId); return resolved.status === 'ok' ? resolved.unitCost : null; },
      foodUom: (foodId) => { const resolved = resolveFood(foodId); return resolved.status === 'ok' ? resolved.uom : null; },
    });
    const included = [...resolvedByFood.values()].reduce((sum, resolved) => sum + resolved.provenance.includedCount, 0);
    const latest = [...resolvedByFood.values()].map((resolved) => resolved.provenance.latestDocumentDate).filter((date): date is string => !!date).sort().at(-1) ?? null;
    return result.status === 'ok' && result.costPerPortion != null
      ? { costPerPortion: result.costPerPortion, source: 'seasonal_history', includedObservations: included, latestDocumentDate: latest }
      : { costPerPortion: null, source: 'unavailable', includedObservations: included, latestDocumentDate: latest };
  }, [recipes, seasonObservations]);

  async function saveQuote(draft: EventQuoteSaveDraft): Promise<void> {
    const tenantId = profile?.active_tenant_id; const businessId = profile?.active_business_id;
    if (!tenantId || !businessId) throw new Error('Nessuna sede attiva');
    const current = editingEvent;
    if (current && (!['draft', 'quoted', 'confirmed'].includes(current.status) || current.discharge_snapshot)) throw new Error('Il preventivo non è modificabile dopo l’avvio della produzione.');
    const revision: CateringQuoteRevision = {
      id: crypto.randomUUID(), revision: (current?.quote_revisions.length ?? 0) + 1, created_at: new Date().toISOString(),
      event_name: draft.eventName, customer_name: draft.customerName, event_date: draft.eventDate,
      season_start: draft.seasonStart, season_end: draft.seasonEnd, as_of: draft.asOf,
      guests_min: draft.guestsMin, guests_expected: draft.guestsExpected, guests_max: draft.guestsMax,
      selected_recipe_ids: draft.selectedRecipeIds, logistics_mode: draft.logisticsMode, destination_id: draft.destinationId,
      input_snapshot: {
        manualMenuCost: draft.manualMenuCost, consumablesPerGuest: draft.consumablesPerGuest, otherVariablePerGuest: draft.otherVariablePerGuest,
        minStaff: draft.minStaff, guestsPerWorker: draft.guestsPerWorker, staffHours: draft.staffHours, loadedHourlyRate: draft.loadedHourlyRate,
        eventCosts: draft.eventCosts, structureShare: draft.structureShare, grossPricePerGuest: draft.grossPricePerGuest, guestVat: draft.guestVat,
        grossRoomRevenue: draft.grossRoomRevenue, roomVat: draft.roomVat, targetProfit: draft.targetProfit,
        forecastAdjustmentPct: draft.forecastAdjustmentPct,
        costBasis: draft.costBasis,
        portionsByRecipe: draft.portionsByRecipe ?? {},
      },
      result_snapshot: draft.scenarios as unknown as Record<string, unknown>,
    };
    const patch = {
      tenant_id: tenantId, business_id: businessId, name: draft.eventName, customer_name: draft.customerName,
      event_date: draft.eventDate, status: current?.status ?? 'quoted', guests_expected: draft.guestsExpected,
      logistics_mode: draft.logisticsMode, destination_id: draft.destinationId,
      menu_lines: draft.selectedRecipeIds.map((id) => ({ recipe_id: id, recipe_name: optionById.get(id)?.name ?? id, portions_per_guest: draft.portionsByRecipe?.[id] ?? 1 })),
      quote_revisions: [...(current?.quote_revisions ?? []), revision], source_document_ids: current?.source_document_ids ?? [],
      shipment_id: current?.shipment_id ?? null, notes: current?.notes ?? null,
      discharge_draft: null,
    };
    await enqueue.mutateAsync({ op: current ? 'update' : 'insert', table: 'catering_events', id: current?.id ?? crypto.randomUUID(), patch, base_version: current?.version ?? 0 });
    setToast(current ? `Revisione ${revision.revision} salvata` : 'Preventivo ed evento salvati');
  }

  async function transition(event: CateringEvent, status: CateringEvent['status']): Promise<void> {
    try {
      await enqueue.mutateAsync({ op: 'update', table: 'catering_events', id: event.id, patch: { status }, base_version: event.version });
      setToast(`Evento: ${STATUS_LABEL[status]}`);
    } catch (error) { setToast(toUserMessage(error)); }
  }

  if (editorId) {
    if (editorId !== 'new' && !editingEvent) return <GatePage>{eventsLoading || events.some((event) => event.id === editorId) ? <><IonSpinner /><p>Caricamento del preventivo…</p></> : <><p>Evento non disponibile nella sede attiva.</p><IonButton routerLink="/business/event-quotes" routerDirection="back">Torna agli eventi</IonButton></>}</GatePage>;
    if (editingEvent && (!['draft', 'quoted', 'confirmed'].includes(editingEvent.status) || editingEvent.discharge_snapshot)) return <GatePage><p>Questo evento non è più modificabile: la produzione è già stata avviata o conclusa.</p><IonButton routerLink="/business/event-quotes" routerDirection="back">Torna agli eventi</IonButton></GatePage>;
    return <EventQuoteSimulator presentation="page" isOpen onClose={() => history.replace('/business/event-quotes')} recipes={options} destinations={destinations} defaultVatRate={defaultVatRate} initialDraft={initialQuoteDraft} onSave={saveQuote} resolveRecipeCost={resolveRecipeSeasonCost} />;
  }

  return (
    <IonPage>
      <IonHeader><IonToolbar><IonButtons slot="start"><IonBackButton defaultHref="/today" /></IonButtons><IonTitle>Sale Trattenimenti / Catering</IonTitle></IonToolbar></IonHeader>
      <IonContent className="ion-padding te-catering-content">
        <div className="te-catering-workspace">
          <header className="te-catering-heading"><div><span className="te-catering-eyebrow">Regia degli eventi</span><h1>Ogni evento, sotto controllo</h1><p>Menu e coperti guidano gli acquisti. I lotti realmente utilizzati completano lo scarico.</p></div><IonButton fill="outline" routerLink="/haccp/food-cost">Apri Food Cost</IonButton></header>
          <nav className="te-catering-tabs" aria-label="Aree del modulo Catering">
            <button type="button" aria-pressed={view === 'events'} onClick={() => setView('events')}>Eventi</button>
            <button type="button" aria-pressed={view === 'procurement'} onClick={() => setView('procurement')}>Approvvigionamenti</button>
            <button type="button" aria-pressed={view === 'production'} onClick={() => setView('production')}>Produzione e scarico</button>
            <button type="button" aria-pressed={view === 'logistics'} onClick={() => setView('logistics')}>DDT e spedizioni</button>
          </nav>

          {view === 'events' ? (
            <>
              <div className="te-catering-guide-launch">
                <div className="te-catering-guide-launch__copy">
                  <strong>Guida al modulo Catering</strong>
                  <span>Dal preventivo al consuntivo di magazzino</span>
                </div>
                <button
                  type="button"
                  className={`te-info-badge te-info-badge--inline${guideOpen ? ' is-open' : ''}`}
                  aria-label="Apri la guida al modulo Sale Trattenimenti e Catering"
                  aria-expanded={guideOpen}
                  onClick={() => setGuideOpen(true)}
                >
                  <span className="te-info-badge__circle" aria-hidden="true">
                    <IonIcon icon={informationOutline} />
                  </span>
                </button>
              </div>
              <IonButton expand="block" style={{ margin: '16px 0' }} routerLink="/business/event-quotes/new">Nuovo preventivo / evento</IonButton>
              <div className="te-catering-metrics" aria-label="Riepilogo operativo">
                <div><span>Eventi nei prossimi 7 giorni</span><strong>{upcomingEvents.length}</strong><small>Esclusi conclusi e annullati</small></div>
                <div><span>Coperti confermati · 7 giorni</span><strong>{upcomingEvents.filter((event) => ['confirmed', 'in_production'].includes(event.status)).reduce((sum, event) => sum + event.guests_expected, 0)}</strong><small>Controlla il numero finale prima degli acquisti</small></div>
                <div><span>Produzioni ancora aperte</span><strong>{events.filter((event) => event.status === 'in_production').length}</strong><small>Da verificare e chiudere, anche di date precedenti</small></div>
              </div>
              <div className="te-catering-filters">
                <IonInput label="Cerca evento, cliente o data" labelPlacement="stacked" fill="outline" type="search" value={search} onIonInput={(change) => setSearch(String(change.detail.value ?? ''))} />
                <IonSelect label="Mostra" labelPlacement="stacked" fill="outline" value={agendaFilter} onIonChange={(change) => setAgendaFilter(change.detail.value as CateringAgendaFilter)}>
                  <IonSelectOption value="active">Da gestire</IonSelectOption><IonSelectOption value="week">Prossimi 7 giorni</IonSelectOption><IonSelectOption value="archive">Archivio</IonSelectOption><IonSelectOption value="all">Tutti gli eventi</IonSelectOption>
                </IonSelect>
              </div>
              {events.length > 0 && <p role="status">{visibleEvents.length} eventi visualizzati{visibleEvents.length === 0 ? ' · Cambia il filtro o la ricerca.' : ''}</p>}
              {events.length === 0 && <IonText color="medium"><p style={{ textAlign: 'center', padding: 24 }}>Nessun evento salvato per questa sede.</p></IonText>}
              {visibleEvents.map((event) => {
                const lastRevision = event.quote_revisions.at(-1);
                const requirements = computeCateringEventRequirements({ event, recipes, foodUomById: canonicalUomByFood });
                const nextAction = cateringNextAction(event, requirements.missing.length);
                const canEdit = ['draft', 'quoted', 'confirmed'].includes(event.status) && !event.discharge_snapshot;
                const expected = lastRevision?.result_snapshot?.expected as { totalCost?: number | null; targetGrossPricePerGuest?: number | null; operatingResult?: number | null } | undefined;
                return (
                  <IonCard className="te-catering-event-card" key={event.id}>
                    <IonCardHeader><IonCardSubtitle>{new Date(`${event.event_date}T12:00:00`).toLocaleDateString('it-IT')} · {STATUS_LABEL[event.status]} · {event.guests_expected} coperti</IonCardSubtitle><IonCardTitle>{event.name}</IonCardTitle></IonCardHeader>
                    <IonCardContent>
                      <p style={{ marginTop: 0 }}>{event.customer_name || 'Cliente non indicato'} · {event.logistics_mode === 'onsite' ? 'in sede' : event.logistics_mode === 'internal_transfer' ? 'trasferimento interno' : 'DDT verso location'}</p>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 8, marginBottom: 12 }}>
                        <div><small>Revisioni</small><b style={{ display: 'block' }}>{event.quote_revisions.length}</b></div>
                        <div><small>Costo previsto</small><b style={{ display: 'block' }}>{expected?.totalCost == null ? '—' : eur.format(expected.totalCost)}</b></div>
                        <div><small>Prezzo obiettivo</small><b style={{ display: 'block' }}>{expected?.targetGrossPricePerGuest == null ? '—' : `${eur.format(expected.targetGrossPricePerGuest)}/ospite`}</b></div>
                        <div><small>Risultato previsto dopo struttura</small><b style={{ display: 'block' }}>{expected?.operatingResult == null ? '—' : eur.format(expected.operatingResult)}</b></div>
                      </div>
                      <p className="te-catering-caption">Stima della revisione {lastRevision?.revision ?? '—'}, non consuntivo. Prezzo obiettivo lordo per ospite; risultato sui ricavi netti e sui costi inseriti.</p>
                      <div className="te-catering-next"><strong>{nextAction.label}</strong><p>{nextAction.detail}</p>
                        {nextAction.area !== 'events' && <IonButton fill="outline" onClick={() => { setSelectedEventId(event.id); setView(nextAction.area); }}>{nextAction.label}</IonButton>}
                      </div>
                      <div className="te-catering-checks"><span>Menu: {event.menu_lines.length} piatti{requirements.missing.length ? ` · ${requirements.missing.length} dati mancanti` : ''}</span><span>DDT di carico: {event.source_document_ids.length}</span><span>Trasporto: {event.logistics_mode === 'onsite' ? 'non previsto' : event.shipment_id ? 'documento collegato' : 'da organizzare'}</span></div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                        {canEdit && <IonButton size="small" fill="outline" routerLink={`/business/event-quotes/${event.id}/edit`}>Modifica preventivo e coperti</IonButton>}
                        {['draft', 'quoted'].includes(event.status) && <IonButton size="small" color="success" disabled={enqueue.isPending} onClick={() => void transition(event, 'confirmed')}>Conferma evento</IonButton>}
                        {event.status === 'confirmed' && <IonButton size="small" disabled={enqueue.isPending || !requirements.lines.length || requirements.missing.length > 0} onClick={() => void transition(event, 'in_production')}>Avvia produzione</IonButton>}
                        {event.status === 'in_production' && <IonButton size="small" color="success" onClick={() => { setSelectedEventId(event.id); setView('production'); }}>Verifica consumi e chiudi</IonButton>}
                        <IonButton size="small" fill="clear" onClick={() => { setSelectedEventId(event.id); setView('production'); }}>Produzione e scarico</IonButton>
                        <IonButton size="small" fill="clear" onClick={() => { setSelectedEventId(event.id); setView('logistics'); }}>DDT / spedizioni</IonButton>
                      </div>
                      <IonNote>{event.source_document_ids.length} document{event.source_document_ids.length === 1 ? 'o' : 'i'} di carico collegat{event.source_document_ids.length === 1 ? 'o' : 'i'}{event.discharge_snapshot ? ' · scarico concluso' : ''}</IonNote>
                    </IonCardContent>
                  </IonCard>
                );
              })}
            </>
          ) : view === 'procurement' ? (
            <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 14, marginTop: 16 }}>
              <h2>Acquisti per gli eventi della settimana</h2><p>Conferma prima menu e coperti con il cliente, poi confronta il fabbisogno con le giacenze. Queste quantità non comprendono gli ordini già inviati ai fornitori e non riservano merce.</p>
              <IonInput label="Settimana dal" labelPlacement="stacked" fill="outline" type="date" value={weekStart} onIonInput={(event) => setWeekStart(String(event.detail.value ?? mondayOf()))} />
              <p>Eventi confermati o in produzione: <b>{procurement.includedEventIds.length}</b> · dal {cateringDateLabel(procurement.weekStart)} al {cateringDateLabel(procurement.weekEnd)}. È un suggerimento: non emette ordini.</p>
              <div className="te-catering-checks">{events.filter((event) => procurement.includedEventIds.includes(event.id)).map((event) => <IonButton key={event.id} fill="outline" onClick={() => { setSelectedEventId(event.id); setView('production'); }}>{cateringDateLabel(event.event_date)} · {event.name} · {event.guests_expected} coperti</IonButton>)}</div>
              {procurement.lines.length === 0 ? <IonText color="medium"><p>Nessun fabbisogno calcolabile per questa settimana.</p></IonText> : (
                <IonList>
                  {procurement.lines.map((line) => {
                    const food = foodById.get(line.foodId); const supplier = food?.current_supplier_id ? supplierById.get(food.current_supplier_id) : null;
                    return <IonItem key={line.foodId}><IonLabel className="ion-text-wrap"><b>{food?.name ?? line.foodId}</b><p>Serve {qty.format(line.requiredQty)} {line.uom} · disponibile {qty.format(line.availableQty)} {line.uom} · {line.eventIds.length} event{line.eventIds.length === 1 ? 'o' : 'i'}</p><IonNote>{supplier ? `Fornitore suggerito: ${supplier}` : 'Fornitore da selezionare'}</IonNote></IonLabel><div slot="end" style={{ textAlign: 'right' }}><small>Da ordinare</small><b style={{ display: 'block', color: line.suggestedOrderQty > 0 ? '#b45309' : '#15803d' }}>{qty.format(line.suggestedOrderQty)} {line.uom}</b></div></IonItem>;
                  })}
                </IonList>
              )}
              {procurement.missing.length > 0 && <div className="te-catering-next" role="status"><b>Lista parziale: {procurement.missing.length} dati da completare</b><p>Non usare queste quantità come ordine definitivo: mancano parte dei fabbisogni.</p><ul>{procurement.missing.map((item, index) => <li key={`${item.eventId}:${item.recipeId}:${index}`}>{events.find((event) => event.id === item.eventId)?.name ?? 'Evento'} · {recipes.find((recipe) => recipe.id === item.recipeId)?.name ?? 'Menu'}: {CATERING_MISSING_LABEL[item.reason]}</li>)}</ul></div>}
            </section>
          ) : view === 'production' ? (
            <CateringProductionTab
              events={events}
              selectedEvent={selectedEvent}
              onSelectEvent={setSelectedEventId}
              recipes={recipes}
              foods={foods}
              lots={lotCandidates}
              documents={documents}
              suppliers={suppliers}
              stockIn={activeStockIn}
              stockOut={stockOut as StockEvt[]}
              canonicalUomByFood={canonicalUomByFood}
              onToast={setToast}
            />
          ) : (
            <CateringLogisticsTab
              events={events}
              selectedEvent={selectedEvent}
              onSelectEvent={setSelectedEventId}
              recipes={recipes}
              foods={foods}
              lots={lotCandidates}
              documents={documents.filter((document) => !document.archived_at && (!document.business_id || document.business_id === profile?.active_business_id))}
              suppliers={suppliers}
              shipments={shipments.filter((shipment) => !shipment.archived_at)}
              canonicalUomByFood={canonicalUomByFood}
              onToast={setToast}
            />
          )}
        </div>
      </IonContent>
      <CateringGuideModal isOpen={guideOpen} onClose={() => setGuideOpen(false)} />
      <IonToast isOpen={!!toast} message={toast ?? ''} duration={2400} onDidDismiss={() => setToast(null)} />
    </IonPage>
  );
}

function CateringGuideModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }): JSX.Element {
  return (
    <IonModal className="te-catering-guide-modal" isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Guida Catering</IonTitle>
          <IonButtons slot="end"><IonButton onClick={onClose}>Chiudi</IonButton></IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <article className="te-catering-guide" aria-labelledby="catering-guide-title">
          <header className="te-catering-guide__intro">
            <h2 id="catering-guide-title">Dal preventivo alla tracciabilità dell’evento</h2>
            <p>Questa guida ti accompagna nella preparazione di un matrimonio o catering, dalla stima economica fino allo scarico dei lotti realmente consumati.</p>
          </header>

          <section className="te-catering-guide__section" aria-labelledby="catering-guide-prerequisites">
            <h2 id="catering-guide-prerequisites" className="te-catering-guide__section-title">Prima di iniziare</h2>
            <ul className="te-catering-guide__list">
              {CATERING_GUIDE_PREREQUISITES.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </section>

          <section className="te-catering-guide__section" aria-labelledby="catering-guide-steps">
            <h2 id="catering-guide-steps" className="te-catering-guide__section-title">Come usarla, passo dopo passo</h2>
            <div className="te-catering-guide__steps">
              {CATERING_GUIDE_STEPS.map((step, index) => (
                <section className="te-catering-guide__step" key={step.id}>
                  <span className="te-catering-guide__step-number" aria-hidden="true">{index + 1}</span>
                  <div className="te-catering-guide__step-copy">
                    <span className="te-catering-guide__step-area">{step.area}</span>
                    <h3>{step.title}</h3>
                    <p>{step.body}</p>
                    <p className="te-catering-guide__result">Risultato: {step.result}</p>
                  </div>
                </section>
              ))}
            </div>
          </section>

          <section className="te-catering-guide__section" aria-labelledby="catering-guide-results">
            <h2 id="catering-guide-results" className="te-catering-guide__section-title">Cosa ottieni usando il modulo</h2>
            <ul className="te-catering-guide__list">
              {CATERING_GUIDE_RESULTS.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </section>

          <section className="te-catering-guide__section te-catering-guide__limits" aria-labelledby="catering-guide-limits">
            <h2 id="catering-guide-limits" className="te-catering-guide__section-title">Da ricordare</h2>
            <ul className="te-catering-guide__list">
              {CATERING_GUIDE_LIMITS.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </section>

          <IonButton className="te-catering-guide__close" expand="block" onClick={onClose}>Ho capito, inizia</IonButton>
        </article>
      </IonContent>
    </IonModal>
  );
}

function CateringEventPicker({ events, selectedEvent, onSelectEvent }: {
  events: CateringEvent[]; selectedEvent: CateringEvent | null; onSelectEvent: (id: string) => void;
}): JSX.Element {
  return <IonSelect label="Evento" labelPlacement="stacked" fill="outline" value={selectedEvent?.id ?? ''} onIonChange={(change) => onSelectEvent(String(change.detail.value ?? ''))}>
    {events.map((event) => <IonSelectOption key={event.id} value={event.id}>{event.event_date} · {event.name} · {STATUS_LABEL[event.status]}</IonSelectOption>)}
  </IonSelect>;
}

function CateringProductionTab(props: {
  events: CateringEvent[];
  selectedEvent: CateringEvent | null;
  onSelectEvent: (id: string) => void;
  recipes: Recipe[];
  foods: FoodRow[];
  lots: CateringLotCandidate[];
  documents: DocumentRow[];
  suppliers: SupplierRow[];
  stockIn: StockEvt[];
  stockOut: StockEvt[];
  canonicalUomByFood: Record<string, string>;
  onToast: (message: string) => void;
}): JSX.Element {
  const { profile } = useAuth();
  const enqueue = useEnqueueMaster();
  const enqueueStockCommand = useEnqueueStockCommand();
  const [lines, setLines] = useState<CateringDischargeLine[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingCommandId = useRef<string | null>(null);
  const event = props.selectedEvent;
  const foodById = useMemo(() => new Map(props.foods.map((food) => [food.id, food])), [props.foods]);
  const recipeById = useMemo(() => new Map(props.recipes.map((recipe) => [recipe.id, recipe])), [props.recipes]);
  const documentById = useMemo(() => new Map(props.documents.map((document) => [document.id, document])), [props.documents]);
  const supplierById = useMemo(() => new Map(props.suppliers.map((supplier) => [supplier.id, supplier])), [props.suppliers]);
  const requirements = useMemo(() => event ? computeCateringEventRequirements({ event, recipes: props.recipes, foodUomById: props.canonicalUomByFood }) : { lines: [], missing: [] }, [event, props.canonicalUomByFood, props.recipes]);
  const allocation = useMemo(() => allocateCateringLots({ requirements: requirements.lines, lots: props.lots, selectedDocumentIds: new Set(event?.source_document_ids ?? []) }), [event?.source_document_ids, props.lots, requirements.lines]);
  const canDischarge = profile?.is_platform_owner === true || profile?.role === 'consulente' || profile?.role === 'ristoratore';

  useEffect(() => {
    if (!event) { setLines([]); return; }
    const saved = event.discharge_snapshot?.lines ?? event.discharge_draft?.lines;
    if (saved?.length) { setLines(saved); return; }
    setLines(allocation.allocations.map((line) => ({
      food_id: line.foodId,
      food_name: foodById.get(line.foodId)?.name ?? line.foodId,
      recipe_ids: line.recipeIds,
      required_qty: line.requiredQty,
      allocated_qty: line.allocatedQty,
      leftover_qty: 0,
      consumed_qty: line.allocatedQty,
      uom: line.uom,
      batch_lot: line.batchLot,
      expiration_date: line.expirationDate,
      supplier_id: line.supplierId,
      document_id: line.documentId,
      storage_unit_id: line.storageUnitId,
    })));
  }, [allocation.allocations, event, foodById]);

  function updateLeftover(index: number, value: string): void {
    const parsed = Math.max(0, Number(value.replace(',', '.')) || 0);
    setLines((current) => current.map((line, currentIndex) => currentIndex !== index ? line : {
      ...line,
      leftover_qty: Math.min(parsed, line.allocated_qty),
      consumed_qty: round6(Math.max(0, line.allocated_qty - Math.min(parsed, line.allocated_qty))),
    }));
  }

  async function saveDraft(): Promise<void> {
    if (!event) return;
    try {
      const draft = CateringDischargeDraftSchema.parse({ version: 1, prepared_at: new Date().toISOString(), prepared_by: profile?.id ?? null, lines });
      await enqueue.mutateAsync({ op: 'update', table: 'catering_events', id: event.id, patch: { discharge_draft: draft }, base_version: event.version });
      props.onToast('Proposta di scarico salvata');
    } catch (error) { props.onToast(toUserMessage(error)); }
  }

  async function confirmDischarge(): Promise<void> {
    if (!event || !profile?.active_tenant_id || !profile.active_business_id) return;
    if (!canDischarge) { props.onToast('Solo il responsabile può confermare lo scarico'); return; }
    if (event.status !== 'in_production') { props.onToast('Avvia prima la produzione dell’evento'); return; }
    if (requirements.missing.length || allocation.shortages.length) { props.onToast('Completa ricette e disponibilità prima di confermare lo scarico.'); return; }
    try {
      const consumed = lines.filter((line) => line.consumed_qty > 0.000_001);
      if (consumed.length === 0) throw new Error('Nessuna quantità effettiva da scaricare');
      const reason = `catering_event_discharge:${event.id}`;
      const existing = props.stockOut.find((row) => row.payload.reason === reason && typeof row.payload.command_id === 'string');
      const commandId = typeof existing?.payload.command_id === 'string' ? existing.payload.command_id : pendingCommandId.current ?? crypto.randomUUID();
      pendingCommandId.current = commandId;
      let commandConfirmation: string | Awaited<ReturnType<typeof enqueueStockCommand.mutateAsync>> = commandId;
      if (!existing) {
        const stockMeta = new Map<string, { unitCost: number | null; currency: string }>();
        for (const row of props.stockIn) {
          const foodId = typeof row.payload.food_id === 'string' ? row.payload.food_id : '';
          const lot = typeof row.payload.batch_lot === 'string' ? row.payload.batch_lot : '';
          if (!foodId) continue;
          const value = Number(row.payload.unit_cost);
          stockMeta.set(`${foodId}|${lot}`, { unitCost: Number.isFinite(value) && value >= 0 ? value : null, currency: typeof row.payload.currency === 'string' ? row.payload.currency : 'EUR' });
        }
        const inputs = consumed.map((line, index) => {
          const stockUom = props.canonicalUomByFood[line.food_id] ?? line.uom;
          const metadata = stockMeta.get(`${line.food_id}|${line.batch_lot ?? ''}`);
          return stockSourceLine({ eventId: event.id, index, line, stockUom, unitCost: metadata?.unitCost ?? null, currency: metadata?.currency ?? 'EUR', operatorName: profile.full_name?.trim() || null });
        });
        const command = StockCommandV1Schema.parse({
          version: 1, command_id: commandId, tenant_id: profile.active_tenant_id,
          movement_class: 'production', economic_effect: 'internal', business_id: profile.active_business_id,
          reason, inputs, outputs: [],
        });
        commandConfirmation = await enqueueStockCommand.mutateAsync(command);
      }
      const confirmation = await getSyncEngine().confirmStockCommandPushed(commandConfirmation);
      if (!confirmation.confirmed) {
        throw new Error(`Scarico non confermato dal server: ${confirmation.reason ?? confirmation.status}`);
      }
      const snapshot = CateringDischargeSnapshotSchema.parse({
        version: 1, confirmed_at: new Date().toISOString(), confirmed_by: profile.id ?? null,
        command_id: commandId, lines,
      });
      const draft = CateringDischargeDraftSchema.parse({ version: 1, prepared_at: new Date().toISOString(), prepared_by: profile.id ?? null, lines });
      await enqueue.mutateAsync({
        op: 'update', table: 'catering_events', id: event.id, base_version: event.version,
        patch: {
          discharge_draft: draft, discharge_snapshot: snapshot, status: 'completed',
          source_document_ids: [...new Set([...event.source_document_ids, ...lines.map((line) => line.document_id).filter((id): id is string => !!id)])],
        },
      });
      pendingCommandId.current = null;
      props.onToast(`Scarico concluso: ${consumed.length} lotti, avanzi registrati`);
    } catch (error) { props.onToast(toUserMessage(error)); }
  }

  return <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 14, marginTop: 16 }}>
    <CateringEventPicker events={props.events} selectedEvent={event} onSelectEvent={props.onSelectEvent} />
    {!event ? <IonText color="medium"><p>Nessun evento disponibile.</p></IonText> : <>
      <p><b>{event.name}</b> · {event.guests_expected} coperti · menu: {event.menu_lines.map((line) => line.recipe_name).join(', ') || 'da completare'}</p>
      <IonNote>Quantità dal menu e dai coperti; lotti proposti per scadenza più vicina, con priorità ai DDT collegati. Verifica la proposta rispetto al lavoro effettivo prima di confermare.</IonNote>
      <div className="te-catering-next"><strong>Che cosa inserire come non utilizzato?</strong><p>Solo materia prima rimasta integra e disponibile nello stesso lotto. I piatti già preparati, gli scarti e le eccedenze di buffet non sono ingredienti da rimettere a magazzino: non sottrarli qui. Se quantità o lotti proposti non corrispondono all’utilizzo reale, non confermare lo scarico.</p></div>
      {!event.discharge_snapshot && requirements.missing.length > 0 && <div role="status"><b>Scarico non confermabile: {requirements.missing.length} dati mancanti.</b><ul>{requirements.missing.map((item, index) => <li key={`${item.recipeId}:${index}`}>{recipeById.get(item.recipeId)?.name ?? 'Menu'}: {CATERING_MISSING_LABEL[item.reason]}</li>)}</ul></div>}
      {!event.discharge_snapshot && allocation.shortages.length > 0 && <p style={{ color: '#b91c1c' }}><b>Giacenza insufficiente:</b> {allocation.shortages.map((line) => `${foodById.get(line.foodId)?.name ?? line.foodId} ${qty.format(line.qty)} ${line.uom}`).join(' · ')}</p>}
      <IonList>
        {lines.map((line, index) => {
          const document = line.document_id ? documentById.get(line.document_id) : null;
          const supplierId = line.supplier_id ?? document?.supplier_id ?? null;
          const supplier = supplierId ? supplierById.get(supplierId) : null;
          return <div className="te-catering-line" key={`${line.food_id}:${line.batch_lot ?? ''}:${index}`}>
          <div>
            <b>{line.food_name}</b>
            <p>Piatti: {line.recipe_ids.map((id) => recipeById.get(id)?.name ?? id).join(', ')}</p>
            <p>Lotto <b>{line.batch_lot ?? 'non indicato'}</b>{line.expiration_date ? ` · scad. ${line.expiration_date}` : ''} · previsto su questo lotto {qty.format(line.allocated_qty)} {line.uom}</p>
            <IonNote>{document ? `${supplier?.name ?? 'Fornitore non indicato'} · ${(document.kind ?? 'DDT').toUpperCase()} ${document.document_number ?? 'senza numero'} del ${document.document_date ?? 'data n/d'}` : 'Documento origine non disponibile'}</IonNote>
          </div>
          <div className="te-catering-line__quantities"><IonInput label={`Non utilizzato (${line.uom})`} aria-label={`${line.food_name}, lotto ${line.batch_lot ?? 'non indicato'}: quantità non utilizzata in ${line.uom}`} labelPlacement="stacked" fill="outline" type="number" min="0" max={line.allocated_qty} value={line.leftover_qty} disabled={!!event.discharge_snapshot} onIonInput={(change) => updateLeftover(index, String(change.detail.value ?? '0'))} />
          <div><small>{event.discharge_snapshot ? 'Scaricato' : 'Da scaricare'}</small><strong>{qty.format(line.consumed_qty)} {line.uom}</strong></div></div>
        </div>;
        })}
      </IonList>
      {!event.discharge_snapshot && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        <IonButton fill="outline" disabled={enqueue.isPending || lines.length === 0} onClick={() => void saveDraft()}>Salva proposta</IonButton>
        <IonButton color="success" disabled={!canDischarge || enqueueStockCommand.isPending || enqueue.isPending || event.status !== 'in_production' || requirements.missing.length > 0 || allocation.shortages.length > 0 || lines.length === 0} onClick={() => setConfirmOpen(true)}>Verifica e conferma lo scarico</IonButton>
      </div>}
      {event.discharge_snapshot && <p style={{ color: '#15803d' }}><b>Scarico concluso</b> il {new Date(event.discharge_snapshot.confirmed_at).toLocaleString('it-IT')} · comando {event.discharge_snapshot.command_id.slice(0, 8)}</p>}
      {event.status === 'confirmed' && <IonNote>Per chiudere lo scarico usa “Avvia produzione” nella scheda Evento.</IonNote>}
    </>}
    <IonAlert isOpen={confirmOpen} header="Confermare lo scarico?" message="Le quantità consumate saranno scaricate dai lotti indicati. Il consuntivo diventa immutabile." buttons={[{ text: 'Annulla', role: 'cancel' }, { text: 'Scarica e chiudi', role: 'confirm', handler: () => void confirmDischarge() }]} onDidDismiss={() => setConfirmOpen(false)} />
  </section>;
}

function CateringLogisticsTab(props: {
  events: CateringEvent[];
  selectedEvent: CateringEvent | null;
  onSelectEvent: (id: string) => void;
  recipes: Recipe[];
  foods: FoodRow[];
  lots: CateringLotCandidate[];
  documents: DocumentRow[];
  suppliers: SupplierRow[];
  shipments: ShipmentRow[];
  canonicalUomByFood: Record<string, string>;
  onToast: (message: string) => void;
}): JSX.Element {
  const enqueue = useEnqueueMaster();
  const event = props.selectedEvent;
  const [documentIds, setDocumentIds] = useState<string[]>([]);
  const [shipmentId, setShipmentId] = useState('');
  const foodById = useMemo(() => new Map(props.foods.map((food) => [food.id, food])), [props.foods]);
  const supplierById = useMemo(() => new Map(props.suppliers.map((supplier) => [supplier.id, supplier])), [props.suppliers]);
  const requirements = useMemo(() => event ? computeCateringEventRequirements({ event, recipes: props.recipes, foodUomById: props.canonicalUomByFood }) : { lines: [], missing: [] }, [event, props.canonicalUomByFood, props.recipes]);
  const suggestions = useMemo(() => event ? suggestCateringDocuments({
    eventDate: event.event_date, requirements: requirements.lines, lots: props.lots,
    documents: props.documents.map((document) => ({ id: document.id, documentDate: document.document_date ?? null })),
  }) : [], [event, props.documents, props.lots, requirements.lines]);
  const suggestionById = useMemo(() => new Map(suggestions.map((suggestion) => [suggestion.documentId, suggestion])), [suggestions]);

  useEffect(() => {
    if (!event) { setDocumentIds([]); setShipmentId(''); return; }
    const automatic = suggestions.filter((suggestion) => suggestion.suggested).map((suggestion) => suggestion.documentId);
    setDocumentIds(event.source_document_ids.length > 0 ? event.source_document_ids : automatic);
    setShipmentId(event.shipment_id ?? '');
  }, [event, suggestions]);

  async function save(): Promise<void> {
    if (!event) return;
    try {
      await enqueue.mutateAsync({ op: 'update', table: 'catering_events', id: event.id, patch: { source_document_ids: documentIds, shipment_id: shipmentId || null }, base_version: event.version });
      props.onToast('DDT e spedizione collegati all’evento');
    } catch (error) { props.onToast(toUserMessage(error)); }
  }

  const relevantDocuments = [...props.documents].sort((left, right) => {
    const a = suggestionById.get(left.id); const b = suggestionById.get(right.id);
    return Number(b?.suggested ?? false) - Number(a?.suggested ?? false)
      || (right.document_date ?? '').localeCompare(left.document_date ?? '');
  });
  return <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 14, marginTop: 16 }}>
    <CateringEventPicker events={props.events} selectedEvent={event} onSelectEvent={props.onSelectEvent} />
    {!event ? <IonText color="medium"><p>Nessun evento disponibile.</p></IonText> : <>
      <p><b>Documenti di carico per {event.name}</b></p>
      <IonNote>I suggeriti contengono ingredienti disponibili del menu e hanno data documento nei 7 giorni precedenti l’evento. Puoi scegliere anche altri documenti. Collegare un DDT dà priorità ai suoi lotti: non significa utilizzarne tutta la merce e non costituisce una prenotazione di magazzino.</IonNote>
      <IonList style={{ maxHeight: 430, overflowY: 'auto', marginTop: 8 }}>
        {relevantDocuments.map((document) => {
          const suggestion = suggestionById.get(document.id);
          const documentLots = props.lots.filter((lot) => lot.documentId === document.id && lot.availableQty > 0);
          const supplierId = document.supplier_id ?? documentLots.find((lot) => lot.supplierId)?.supplierId ?? null;
          const supplier = supplierId ? supplierById.get(supplierId) : null;
          const contents = documentLots.map((lot) => `${foodById.get(lot.foodId)?.name ?? lot.foodId} · lotto ${lot.batchLot ?? 'n/d'} · disp. ${qty.format(lot.availableQty)} ${lot.uom}`);
          return <IonItem key={document.id}>
            <IonCheckbox slot="start" aria-label={`Collega ${supplier?.name ?? 'fornitore non indicato'}, documento ${document.document_number ?? 'senza numero'} del ${document.document_date ?? 'data non indicata'}`} checked={documentIds.includes(document.id)} disabled={!!event.discharge_snapshot} onIonChange={(change) => setDocumentIds((current) => change.detail.checked ? [...new Set([...current, document.id])] : current.filter((id) => id !== document.id))} />
            <IonLabel className="ion-text-wrap">
              <b>{supplier?.name ?? 'Fornitore non indicato'} · {(document.kind ?? 'DDT').toUpperCase()} {document.document_number ?? 'senza numero'}</b>
              <p>{document.document_date ?? 'data non indicata'}{suggestion ? ` · ${suggestion.matchingFoodIds.length} ingredient${suggestion.matchingFoodIds.length === 1 ? 'e' : 'i'} del menu` : ' · nessun ingrediente del menu disponibile'}</p>
              {contents.length > 0 ? <IonNote>{contents.join(' | ')}</IonNote> : <IonNote>Nessun lotto disponibile collegato a questo documento</IonNote>}
            </IonLabel>
            {suggestion?.suggested && <IonBadge color="success" slot="end">Suggerito</IonBadge>}
          </IonItem>;
        })}
      </IonList>
      <IonSelect label="DDT in uscita / spedizione verso la sala" labelPlacement="stacked" fill="outline" value={shipmentId} disabled={!!event.discharge_snapshot} onIonChange={(change) => setShipmentId(String(change.detail.value ?? ''))}>
        <IonSelectOption value="">{event.logistics_mode === 'onsite' ? 'Nessuna: evento in sede' : 'Da collegare'}</IonSelectOption>
        {props.shipments.map((shipment) => <IonSelectOption key={shipment.id} value={shipment.id}>{shipment.kind?.toUpperCase() ?? 'SPEDIZIONE'} {shipment.doc_number ? `${shipment.doc_number}/${shipment.doc_year}` : '(bozza)'} · {shipment.status ?? 'stato n/d'}</IonSelectOption>)}
      </IonSelect>
      {event.logistics_mode !== 'onsite' && <p>Il DDT di carico documenta l’acquisto dal fornitore. Il DDT in uscita accompagna il trasporto verso la sala: sono due documenti distinti. Prima della partenza verifica destinazione, quantità e stato della spedizione nel modulo dedicato.</p>}
      {!event.discharge_snapshot && <IonButton expand="block" style={{ marginTop: 10 }} disabled={enqueue.isPending} onClick={() => void save()}>Salva collegamenti evento</IonButton>}
      {event.logistics_mode !== 'onsite' && <IonButton expand="block" fill="outline" routerLink="/warehouse/shipments">Crea o modifica DDT in uscita</IonButton>}
    </>}
  </section>;
}
